/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useEffect, useRef, useState,
} from 'react';
import {
    any, object, func, string,
} from 'prop-types';
import mbpUtil from 'mbp-api-util';
import mbpLogger from 'mbp-logger';
import { useSelector, useDispatch } from 'react-redux';
import { getAccessToken } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { trackEvent } from '../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ConversationalQualtricsFeedBackContainer from './ConversationalUIFeedbackContainer';
import getTrustedOrigin from './helper/TrustedOrigin';

let eventLogic;
const ConversationalUIChatBot = ({
    deviceType, brand, browser, operatingSystem, brandTheme, pageType, location, setIsFloraChatBtnClicked, featureFlags,
}) => {
    const iframeRef = useRef(null);
    const pageTypeRef = useRef(pageType);
    const [isIframeMaximized, setIsIframeMaximized] = useState(false);
    const isMobile = deviceType !== 'desktop';
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);
    const trustedOrigin = getTrustedOrigin(mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const cartItems = useSelector((state) => state?.cart?.recipients);
    const cartHasPassport = useRef('false');
    const orderDetails = useSelector((state) => state?.cart);
    const ORDER_PLACED = 'Published';
    const dispatch = useDispatch();
    const [orderSize, setOrderSize] = useState(0);
    const [currentLocation, setCurrentLocation] = useState(undefined);
    const accessToken = useSelector(getAccessToken);
    const eventLogicFlag = featureFlags['is-conv-ui-event-logic-enabled'];
    const isFloraChatQualtricsSurveyButtonEnabled = featureFlags['is-flora-chat-persistent-qualtrics-survey-button-enabled'];
    const [feedbackMinimized, setFeedbackMinimized] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [isFeedbackButtonDisplayed, setIsFeedbackButtonDisplayed] = useState(isFloraChatQualtricsSurveyButtonEnabled);
    const [isChatDisplayed, setIsChatDisplayed] = useState(!isMobile);
    useEffect(() => {
        if (isIframeMaximized) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isIframeMaximized]);

    useEffect(() => {
        if (accessToken) {
            try {
                const auth = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.auth);
                const iframe = iframeRef.current;
                if (iframe && iframe?.contentWindow) {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-access-token', data: auth },
                        `${trustedOrigin}/chat/flora`,
                    );
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Token Issue - ERROR: ${error}`,
                });
            }
        }
    }, [accessToken]);

    useEffect(() => {
        cartItems?.forEach((cartItem) => {
            cartItem?.cartItems?.forEach((item) => {
                if (item?.itemInfo?.lineItemType === 'PASSPORT') {
                    cartHasPassport.current = 'true';
                }
            });
        });
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-cart-passport-status', data: { cartHasPassport: cartHasPassport.current } },
                `${trustedOrigin}/chat/flora`,
            );
        }
    }, [cartItems]);

    useEffect(() => {
        pageTypeRef.current = pageType;
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-page-info', data: pageTypeRef.current }, `${trustedOrigin}/chat/flora`,
            );
        }
    }, [pageType]);

    /**
         * send current url to the connected agent whenever the user change the location
         */
    useEffect(() => {
        const path = location?.pathname;
        if (currentLocation !== path) {
            setCurrentLocation(window?.location?.href);
            const customParameters = {
                currentUrl: window?.location?.href,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-visiting-page', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [location?.pathname]);

    /**
     * send order details to the connected agent whenever the user add or remove something.
     */
    useEffect(() => {
        if (orderDetails && orderDetails.orderSize !== orderSize) {
            const itemAdded = orderDetails.orderSize > orderSize;
            setOrderSize(orderDetails.orderSize);
            const customParameters = {
                isItemAdded: itemAdded,
                orderValue: orderDetails.cartTotal,
                orderNumber: orderDetails.orderIdWithSourceId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-cart-details', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [orderDetails]);

    useEffect(() => {
        if (orderDetails?.cartStatus && orderDetails?.cartStatus === ORDER_PLACED) {
            const customParameters = {
                wasOrderPlaced: 'true',
                orderNumber: orderDetails?.cartId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-order-placed-status', data: customParameters },
                    `${trustedOrigin}/chat/flora`,
                );
            }
        }
    }, [orderDetails?.cartStatus]);

    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const iframe = iframeRef.current;
                if (event?.data?.type && typeof event?.data?.type === 'string' && event?.data?.type.includes('flora')) {
                    let token = accessToken;
                    const env = (mbpUtil.getEnv('APP_GRAPHQL_ENV') || '')?.toLowerCase();
                    if (env && !env.includes('prod')) {
                        token = await getAccessTokenSafely();
                    }
                    if (eventLogicFlag) {
                        if (event.data.type === 'flora-pwa-events-logic') {
                            const functionBody = JSON.parse(event.data.data);
                            // eslint-disable-next-line no-new-func
                            eventLogic = new Function('event', 'brand', 'browser', 'deviceType', 'operatingSystem', 'brandTheme', 'trustedOrigin', 'setIsIframeMaximized', 'setIsFloraChatBtnClicked', 'iframeRef', 'pageTypeRef', 'isMobile', 'token', functionBody);
                        }
                        if (eventLogic) {
                            eventLogic(event, brand, browser, deviceType, operatingSystem, brandTheme, trustedOrigin, setIsIframeMaximized, setIsFloraChatBtnClicked, iframeRef, pageTypeRef, isMobile, token);
                        }
                        if (isFloraChatQualtricsSurveyButtonEnabled) {
                            if (event.data.type === 'chat-iframe-maximised') {
                                setIsFeedbackButtonDisplayed(false);
                                if (feedbackOpen === true) setFeedbackMinimized(!feedbackMinimized);
                                setFeedbackOpen(false);
                            } else if (event.data.type === 'chat-iframe-minimised') {
                                setIsFeedbackButtonDisplayed(true);
                            }
                        }
                        if (isMobile) {
                            if (event.data.type === 'flora-chat-iframe-maximised' && !isChatDisplayed) {
                                setIsChatDisplayed(true);
                            }
                            if (event?.data?.type === 'flora-end-chat-hide-button') {
                                setIsChatDisplayed(false);
                            }
                        }
                        if (event.data.type === 'flora-chat-trigger-event-tracking') {
                            const trackingData = event?.data?.tracking_data;
                            if (trackingData) {
                                dispatch(trackEvent(trackingData));
                            }
                        }
                    } else {
                        if (event.data.type === 'flora-conv-ui-session-info') {
                            const url = window.location.href;
                            const auth = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.auth);
                            const profile = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.profile);
                            const enterprise = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.enterpriseId);
                            const browseruuid = localStorage?.getItem('browserUUID') || '';
                            event.source.postMessage({
                                type: 'flora-session-storage-data',
                                data: {
                                    url,
                                    brand,
                                    browser,
                                    deviceType,
                                    operatingSystem,
                                    brandTheme,
                                    auth,
                                    profile,
                                    enterprise,
                                    browseruuid,
                                    token,
                                },
                            }, `${trustedOrigin}/chat/flora`);
                        }
                        if (event.data.type === 'flora-search-suggestion-submitted') {
                            if (iframe && iframe.contentWindow) {
                                iframe.contentWindow.postMessage(
                                    { type: 'flora-update-chat-message-search', data: event.data },
                                    `${trustedOrigin}/chat/flora`,
                                );
                            }
                        }
                        if (event.data.type === 'flora-footer-suggestion-submitted') {
                            if (iframe && iframe.contentWindow) {
                                iframe.contentWindow.postMessage(
                                    { type: 'flora-update-chat-message-footer', data: event.data },
                                    `${trustedOrigin}/chat/flora`,
                                );
                            }
                        }
                        if (event.data.type === 'flora-get-access-token' && iframe?.contentWindow) {
                            iframe.contentWindow.postMessage(
                                { type: 'flora-update-access-token', data: token },
                                `${trustedOrigin}/chat/flora`,
                            );
                        }
                        if (event.data.type === 'flora-get-page-info') {
                            if (iframe && iframe.contentWindow) {
                                iframe.contentWindow.postMessage(
                                    { type: 'flora-update-page-info', data: pageTypeRef.current },
                                    `${trustedOrigin}`,
                                );
                            }
                        }
                        if (event.data.type === 'flora-chat-iframe-maximised') {
                            if (isMobile) {
                                setIsIframeMaximized(() => true);
                                iframeRef.current.style.top = '0';
                                iframeRef.current.style.height = '100dvh';
                                iframeRef.current.style.width = '100vw';

                                iframeRef.current.style.right = null;
                                iframeRef.current.style.bottom = null;
                            } else {
                                iframeRef.current.style.height = '725px';
                                iframeRef.current.style.width = '400px';
                                iframeRef.current.style.bottom = '-5%';
                                if (isFloraChatQualtricsSurveyButtonEnabled) {
                                    setIsFeedbackButtonDisplayed(false);
                                    if (feedbackOpen === true) setFeedbackMinimized(!feedbackMinimized);
                                    setFeedbackOpen(false);
                                }
                            }
                        }
                        if (event.data.type === 'flora-chat-iframe-minimised') {
                            setIsIframeMaximized(() => false);
                            iframeRef.current.style.height = '120px';
                            iframeRef.current.style.width = '80px';
                            iframeRef.current.style.top = '';
                            iframeRef.current.style.bottom = '10px';
                            iframeRef.current.style.right = '0px';
                            if (isFloraChatQualtricsSurveyButtonEnabled && !isMobile) {
                                setIsFeedbackButtonDisplayed(true);
                            }
                        }
                        if (event.data.type === 'flora-chat-preview-msg-closed') {
                            iframeRef.current.style.height = '120px';
                            iframeRef.current.style.width = '80px';
                            iframeRef.current.style.right = '0%';
                        }
                        if (event.data.type === 'flora-preview-msg-ready') {
                            iframeRef.current.style.width = '350px';
                            iframeRef.current.style.height = '150px';
                        }
                        if (event.data.type === 'flora-chat-trigger-event-tracking') {
                            const trackingData = event?.data?.tracking_data;
                            if (trackingData) {
                                dispatch(trackEvent(trackingData));
                            }
                        }
                    }
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Could not post value - ERROR: ${error}`,
                });
            }
        };
        if (isIframeLoaded) {
            window?.addEventListener('message', handleMessage);
        }
        return () => {
            window?.removeEventListener('message', handleMessage);
        };
    }, [isIframeLoaded, trustedOrigin, eventLogicFlag]);

    const handleIframeLoad = () => setIsIframeLoaded(true);
    const handleIframeError = () => setIsIframeLoaded(false);

    return isIframeLoaded ? (
        <>
            <ConversationalQualtricsFeedBackContainer featureFlags={featureFlags} isMobile={isMobile} brand={brand} feedbackMinimized={feedbackMinimized} setFeedbackMinimized={setFeedbackMinimized} feedbackOpen={feedbackOpen} setFeedbackOpen={setFeedbackOpen} isFeedbackButtonDisplayed={isFeedbackButtonDisplayed} />
            <iframe
                id="18fchatbot"
                ref={iframeRef}
                title="18fchatbot"
                allowFullScreen
                style={{
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: 'none',
                    position: 'fixed',
                    zIndex: 2243000,
                    bottom: '10px',
                    right: '0%',
                    width: '120px',
                    height: '80px',
                    display: isChatDisplayed ? '' : 'none',
                }}
                src={`${trustedOrigin}/chat/flora`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        </>
    ) : null;
};

ConversationalUIChatBot.propTypes = {
    deviceType: string.isRequired,
    brand: object.isRequired,
    browser: func.isRequired,
    operatingSystem: func.isRequired,
    brandTheme: any.isRequired,
    pageType: string.isRequired,
    location: object.isRequired,
    setIsFloraChatBtnClicked: func.isRequired,
    featureFlags: object.isRequired,
};
export default ConversationalUIChatBot;
